import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  categoryMeta,
  genreOptions,
  regionOptions,
  yearOptions,
} from "@/mocks/movies";
import {
  filterMovies,
  getMoviesByType,
  sortMovies,
  type SortKey,
} from "@/utils/movieUtils";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilterPanel from "./components/FilterPanel";

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const isValid = type === "movie" || type === "series";
  const meta = isValid ? categoryMeta[type as "movie" | "series"] : null;

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [genre, setGenre] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setRegion("全部");
    setYear("全部");
    setGenre("全部");
    setSort("latest");
    setPage(1);
  }, [type]);

  useEffect(() => {
    setPage(1);
  }, [region, year, genre, sort]);

  const filtered = useMemo(() => {
    if (!isValid) return [];
    const base = getMoviesByType(type as string);
    return sortMovies(filterMovies(base, { region, year, genre }), sort);
  }, [isValid, type, region, year, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setGenre("全部");
  };

  if (!isValid || !meta) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-error-warning-line"
          title="分类不存在"
          description="该分类页面不存在，请返回首页选择电影或电视剧频道。"
          actionLabel="返回首页"
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "分类" },
          { label: meta.name },
        ]}
      />

      <div className="mt-4 rounded-lg bg-secondary-900 px-6 py-8 md:px-10 md:py-12">
        <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
          {meta.name}
          <span className="ml-2 text-base font-normal text-background-300">
            - 日本在线观看
          </span>
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200/90">
          {meta.intro}
        </p>
        <div className="mt-4 flex items-center gap-2 text-sm text-background-300">
          <i className="ri-links-line" aria-hidden="true"></i>
          当前筛选结果：
          <strong className="font-semibold text-accent-400">
            {filtered.length}
          </strong>
          部
        </div>
      </div>

      <div className="mt-6">
        <FilterPanel
          region={region}
          year={year}
          genre={genre}
          sort={sort}
          regionOptions={regionOptions}
          yearOptions={yearOptions}
          genreOptions={genreOptions}
          onRegion={setRegion}
          onYear={setYear}
          onGenre={setGenre}
          onSort={setSort}
          onReset={resetFilters}
        />
      </div>

      <div className="mt-6">
        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-3 md:gap-5 sm:grid-cols-3 lg:grid-cols-4">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <Pagination
              current={page}
              totalPages={totalPages}
              onChange={(p) => {
                setPage(p);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </>
        ) : (
          <EmptyState
            icon="ri-search-eye-line"
            title="暂无符合条件的作品"
            description="换个筛选条件试试，或者点击下方按钮重置全部筛选。"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        )}
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link
          to="/"
          className="flex items-center gap-1 rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
          返回首页
        </Link>
      </div>
    </div>
  );
}